import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Loader2, Swords, Wrench } from "lucide-react";

import { toast } from "@/shared/hooks/use-toast";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";

import { bizAdvisorService } from "../../api/bizAdvisorService";
import type { BattlePlan, Play } from "../../api/bizAdvisorService";
import { KbSaveDialog } from "./KbSaveDialog";
import { errorToast } from "@/shared/lib/apiErrors";

const ENGINE_LABEL: Record<string, string> = {
  poster: "海报",
  video: "视频",
  cs_kb: "客服 FAQ",
  copy: "文案",
  none: "—",
};

function engineLabel(e: string): string {
  return ENGINE_LABEL[e] ?? e;
}

function PlayCard({ play, diagnosisId }: { play: Play; diagnosisId?: number | null }) {
  const navigate = useNavigate();
  const top = play.is_top_leverage;
  const engine = play.suggested_engine;
  const isVisual = engine === "poster" || engine === "video";
  const isText = engine === "cs_kb" || engine === "copy";
  const [kbOpen, setKbOpen] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  // 派引擎可用矩阵(B3 D):有诊断 id + (视觉类 | 文本类且有执行物);none / 缺 id / 文本无 artifacts → disabled。
  const canDispatch = !!diagnosisId && (isVisual || (isText && play.artifacts.length > 0));
  // B2 #25(0927 K6):5 个「派引擎」里 2 个灰着却没说为什么(原因只挂在包装 span,按钮本体 title=null)。
  //   按具体原因分句,同时挂在包装 span(hover)与按钮本体(读屏 / DOM 巡检)。
  const dispatchBlockReason = !diagnosisId
    ? "先生成诊断,才能把打法派给引擎"
    : isText && play.artifacts.length === 0
      ? "该打法没有可执行的生成物(话术 / 文案),不能派"
      : null;

  const onDispatch = async () => {
    if (!diagnosisId) return;
    if (isVisual) {
      // 记 provenance + baseline,再深链跳引擎 create 页(brief 走 location.state 不入 URL 明文)。
      setDispatching(true);
      try {
        await bizAdvisorService.dispatch(diagnosisId, {
          playKey: play.multiplier,
          engine,
          brief: play.engine_brief,
        });
        navigate(
          engine === "poster"
            ? "/ai-haibao/create?source=advisor"
            : "/ai-video/create?source=advisor",
          {
            state: { prefillPrompt: play.engine_brief },
          }
        );
      } catch (e: unknown) {
        toast(errorToast(e, { fallbackTitle: "派引擎失败" }));
        setDispatching(false);
      }
    } else if (isText) {
      setKbOpen(true);
    }
  };

  return (
    <Card className={top ? "border-amber-300 bg-amber-50/60" : ""}>
      <CardContent className="space-y-2 p-5">
        <div className="flex items-center gap-2">
          <span className="font-medium">{play.title}</span>
          <Badge variant="outline">{play.label}</Badge>
          {top && <Badge className="bg-amber-500 text-white">最高杠杆</Badge>}
        </div>
        {play.tactic && <p className="text-sm text-muted-foreground">{play.tactic}</p>}
        {play.steps.length > 0 && (
          <ul className="list-disc space-y-0.5 pl-5 text-sm">
            {play.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        )}
        {play.rationale && (
          <div className="text-xs text-muted-foreground">依据:{play.rationale}</div>
        )}

        {play.artifacts.length > 0 && (
          <div className="space-y-2 rounded-md bg-background/70 p-3">
            <div className="text-xs font-medium text-amber-700">可直接用的执行物</div>
            {play.artifacts.map((a, i) => (
              <div key={i} className="space-y-1 border-b pb-2 last:border-0 last:pb-0">
                <div className="text-sm font-medium">{a.title}</div>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{a.content}</p>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => {
                    navigator.clipboard?.writeText(a.content);
                  }}
                >
                  <Copy className="mr-1 h-3 w-3" /> 复制
                </Button>
              </div>
            ))}
          </div>
        )}

        {engine !== "none" && (
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="secondary">建议引擎:{engineLabel(engine)}</Badge>
            {/* disabled 按钮 pointer-events-none,不可派原因挂包装 span 才弹得出 */}
            <span title={dispatchBlockReason ?? undefined} className="inline-block">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!canDispatch || dispatching}
                title={
                  dispatchBlockReason ??
                  (canDispatch ? `一键把该打法派给「${engineLabel(engine)}」生成执行物` : undefined)
                }
                className="h-7"
                onClick={() => void onDispatch()}
              >
                {dispatching ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Wrench className="mr-1 h-3 w-3" />
                )}
                派引擎
              </Button>
            </span>
          </div>
        )}

        {kbOpen && diagnosisId && (
          <KbSaveDialog
            open={kbOpen}
            onOpenChange={setKbOpen}
            engine={engine}
            play={play}
            diagnosisId={diagnosisId}
          />
        )}
      </CardContent>
    </Card>
  );
}

export function BattleMapSection({
  battlePlan,
  diagnosisId,
}: {
  battlePlan?: BattlePlan | null;
  diagnosisId?: number | null;
}) {
  // 排序与高亮已由后端处理(最高杠杆排首 + is_top_leverage flag),组件无需 topLeverage 入参
  if (!battlePlan || battlePlan.plays.length === 0) return null;
  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Swords className="h-4 w-4 text-amber-600" /> 作战地图
        </div>
        {battlePlan.headline && (
          <div className="rounded-md bg-amber-50/60 p-3 text-sm text-amber-900/90">
            {battlePlan.headline}
          </div>
        )}
        <div className="space-y-3">
          {battlePlan.plays.map((p) => (
            <PlayCard key={p.multiplier} play={p} diagnosisId={diagnosisId} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
